import { Button } from "@mui/material";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";

import { PurchaseStatus } from "@/app/payments/types";
import { BannersPlacement } from "@/components/BannersPlacement/BannersPlacement";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { PromoCodeForm } from "@/components/PromoCodeForm/PromoCodeForm";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import { OverviewCard } from "@/features/trips/components/bookingFlow/summary/OverviewCard";
import { PriceDetailsCard } from "@/features/trips/components/bookingFlow/summary/PriceDetailsCard";
import { TermsAndConditionsCard } from "@/features/trips/components/bookingFlow/summary/TermsAndConditionsCard";
import { TripDetailsCard } from "@/features/trips/components/bookingFlow/summary/TripDetailsCard";
import { TripTourCard } from "@/features/trips/components/bookingFlow/tripDetails/TripTourCard";
import {
  TravellerListCard,
  TravellerListItem,
} from "@/features/trips/components/TravellerListCard";
import { useTripBooking } from "@/features/trips/hooks/useTripBooking";
import { useTripPrice } from "@/features/trips/hooks/useTripPrice";
import { tripQueryOptions } from "@/features/trips/hooks/useTrips";
import type { BookingData } from "@/features/trips/types/types";
import { usePromoCodeValidation } from "@/hooks/usePromoCodeValidation";
import { rudderAnalytics, TripTrackingEvents } from "@/lib/rudderstack";
import { BannersPlacementSlug } from "@/types/enums";
import { allTravellerAgeGroups, type Traveller } from "@/types/types";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";
import { maskPassportNumber } from "@/utils/maskPassportNumber";

export const Route = createFileRoute("/_authenticated/trips/$tripId/summary")({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations();
  const { tripId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: trip } = useSuspenseQuery(tripQueryOptions(tripId));
  const { updatePurchaseState } = usePurchaseStateContext();
  const { getFieldState, formState, handleSubmit, control } =
    useFormContext<BookingData>();
  const { invalid: termsAndConditionsInvalid } = getFieldState(
    "termsAndConditions",
    formState,
  );
  const { basePrice, discountAmount, tourPrice, totalPrice } =
    useTripPrice(trip);
  const { data: travellers } = useQuery(travellersQueryOptions());
  const travellerSlots = useWatch<BookingData, "selectedTravellers">({
    name: "selectedTravellers",
    control,
  });
  const promoCode = useWatch<BookingData, "promoCode">({
    name: "promoCode",
    control,
  });

  const travellersArray = useMemo(() => {
    const travellersById: Record<string, Traveller> = {};
    for (const traveller of travellers || []) {
      travellersById[traveller.id] = traveller;
    }

    return allTravellerAgeGroups
      .map((ageGroup) =>
        travellerSlots[ageGroup]
          .filter((x): x is NonNullable<typeof x> => Boolean(x))
          .map((travellerId, index) => {
            const traveller = travellersById[travellerId];

            return {
              id: traveller.id,
              name: `${traveller.firstName} ${traveller.lastName}`,
              additionalData: [
                t("travellers.travellerTypeN", {
                  type: ageGroup,
                  number: index + 1,
                }),
                maskPassportNumber(traveller.passportNumber),
              ],
            };
          }),
      )
      .flat();
  }, [travellers, travellerSlots, t]);

  const {
    qiPayment: { isPending: isQiPaymentPending, mutateAsync },
  } = useTripBooking({
    onPaymentSuccess: ({ orderId }) => {
      rudderAnalytics.track(TripTrackingEvents.USER_SUCCESSFULY_PAID, {
        trip_id: tripId,
        trip_name: trip.name,
        order_id: orderId,
      });
    },
    onPaymentFailure: ({ errorType, errorMessage }) => {
      rudderAnalytics.track(TripTrackingEvents.USER_UNSUCCESSFULY_PAID, {
        trip_id: tripId,
        trip_name: trip.name,
        error_type: errorType,
        error_message: errorMessage,
      });
    },
    onSuccess: ({ orderId, status }) => {
      if (status === PurchaseStatus.SUCCESSFUL) {
        navigate({ to: "../success", search: { orderId } }).then(() => {
          updatePurchaseState("completed");
        });
      }
    },
  });

  const { data: promoCodeData, validationStatus: promoCodeValidationStatus } =
    usePromoCodeValidation({
      service: "trips",
      validationRequestData: {
        code: promoCode || "",
        tripId,
        price: totalPrice,
      },
    });

  useEffect(() => {
    rudderAnalytics.track(TripTrackingEvents.USER_REVIEWED_TRIP_SUMMARY, {
      trip_id: tripId,
      trip_name: trip.name,
      total_price: basePrice,
    });
  }, [tripId, trip.name, basePrice]);

  return (
    <>
      <h2 className="mb-4 text-xl font-medium text-text-title">
        {t("trips.summaryPage.title")}
      </h2>

      <div className="space-y-3">
        <OverviewCard trip={trip} />

        <TripDetailsCard trip={trip} />

        {isFeatureEnabled(Feature.OptionalTours) && trip.hasOptionalTours && (
          <TripTourCard trip={trip} source="summary" />
        )}

        <TravellerListCard>
          {travellersArray.map(({ id, name, additionalData }) => (
            <TravellerListItem
              key={id}
              name={name}
              additionalData={additionalData}
            />
          ))}
        </TravellerListCard>

        <BannersPlacement
          slug={BannersPlacementSlug.CHECKOUT}
          classNames={{
            wrapper: "-mx-4",
          }}
        />

        {isFeatureEnabled(Feature.PromoCodes) ? (
          <Controller
            name="promoCode"
            control={control}
            render={({ field: { value, onChange } }) => (
              <PromoCodeForm
                appliedCode={value}
                validationStatus={promoCodeValidationStatus}
                onCodeApply={({ code }) => {
                  onChange(code);
                }}
                onCodeRemove={() => {
                  onChange("");
                }}
              />
            )}
          />
        ) : null}

        <PriceDetailsCard
          basePrice={basePrice}
          baseDiscount={discountAmount}
          tourPrice={tourPrice}
          promoCodeDiscount={promoCodeData?.discount}
          totalPrice={promoCodeData?.finalPrice || totalPrice}
        />

        <Controller<BookingData, "termsAndConditions">
          name="termsAndConditions"
          render={({ field: { onChange, onBlur, value } }) => (
            <TermsAndConditionsCard
              checked={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
        />
      </div>

      <PersistentDrawer>
        <Button
          fullWidth
          variant="contained"
          disabled={
            termsAndConditionsInvalid ||
            promoCodeValidationStatus?.type === "loading"
          }
          loading={isQiPaymentPending}
          onClick={() => {
            handleSubmit((data) =>
              mutateAsync({
                packageId: tripId,
                startDate: data.tripDate,
                travelersIds: Object.values(data.selectedTravellers)
                  .flat()
                  .filter((x): x is NonNullable<typeof x> => Boolean(x)),
                numberOfAdultsSingleRoom: data.travellers.adultInSingleRoom,
                numberOfAdultsDoubleRoom: data.travellers.adultInDoubleRoom,
                numberOfChildrenWithBed: data.travellers.childWithBed,
                numberOfChildrenWithoutBed: data.travellers.childWithoutBed,
                numberOfInfants: data.travellers.infant,
                promoCode: promoCodeData?.isValid
                  ? promoCode || undefined
                  : undefined,
                // TODO: Uncomment once BE (DZR-1348) ships and codegen includes this field
                // includesCityTour: data.includesCityTour,
              }),
            )();
          }}
        >
          {t("trips.book")}
        </Button>
      </PersistentDrawer>
    </>
  );
}
